import {
  directAncestorDepthFromTitle,
  resolveCanonicalTreeDirectAncestorEvidence,
} from '@/governor/tree/CanonicalTreeTopologyAuthority';

import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

export type ExistingTreeAuditSeverity =
  | 'info'
  | 'warning'
  | 'blocking';

export type ExistingTreeAuditKind =
  | 'dangling-parent-reference'
  | 'self-parent-reference'
  | 'excessive-parent-count'
  | 'unattached-claimed-profile'
  | 'direct-ancestor-depth-mismatch'
  | 'nickname-preference-available';

export interface ExistingTreeAuditMember {
  id: string;
  ownerUserId: string;
  claimedByUserId: string | null;
  name: string;
  nickname: string | null;
  treeNamePreference: 'real-name' | 'nickname';
  relationship: string | null;
  relationshipOverride: string | null;
  approvalStatus?: string | null;
  profileState?: string | null;
  side?: string | null;
  parentIds: string[];
  spouseId: string | null;
}

export interface ExistingTreeAuditFinding {
  kind: ExistingTreeAuditKind;
  severity: ExistingTreeAuditSeverity;
  memberId: string;
  memberName: string;
  relatedIds: string[];
  summary: string;
  automaticRepairAllowed: false;
}

export interface ExistingTreeStructuralAudit {
  scanned: number;
  findingCount: number;
  blockingCount: number;
  warningCount: number;
  informationalCount: number;
  findings: ExistingTreeAuditFinding[];
  productionWritesAllowed: false;
}


/*
 * CC-TREE-LIVE-003G3B
 *
 * LEGACY_EXTENDED_GENERATION_CONTRADICTION_PROVENANCE_AUTHORITY
 *
 * Identity/reference evidence and ancestry-endpoint evidence are
 * deliberately different authorities.
 *
 * A photo, memory, family unit, merge request, or identity mention may
 * prove that a person existed in family context. It does NOT prove the
 * descendant endpoint through which that person belongs in ancestry.
 *
 * Only evidence that explicitly preserves parent-edge semantics can
 * contribute endpoint authority.
 */
export type CanonicalTreeLegacyGenerationProvenanceStatus =
  | 'NO_CONTRADICTION'
  | 'BLOCKED_INVALID_CONTRADICTION'
  | 'BLOCKED_EVIDENCE_SCOPE_MISMATCH'
  | 'PROVENANCE_ABSENT'
  | 'PROVENANCE_INSUFFICIENT'
  | 'PROVENANCE_AMBIGUOUS'
  | 'PROVEN_UNAMBIGUOUS_ENDPOINT';

export type CanonicalTreeLegacyGenerationProvenanceSource =
  | 'TREE_MUTATION_HISTORY'
  | 'DELETION_SNAPSHOT'
  | 'LEGACY_PARENT_ARRAY_BACKUP'
  | 'RELATIONSHIP_HISTORY'
  | 'PARENT_CHILD_EDGE_HISTORY'
  | 'FAMILY_UNIT'
  | 'MERGE_REQUEST'
  | 'MEDIA_OR_MEMORY'
  | 'OTHER';

export type CanonicalTreeLegacyGenerationEvidenceSemantics =
  | 'EXPLICIT_PARENT_EDGE'
  | 'IDENTITY_REFERENCE_ONLY';

export interface CanonicalTreeLegacyGenerationContradiction {
  readonly ownerUserId: string;
  readonly memberId: string;
  readonly structuralDepth: number;
  readonly claimedDepth: number;
}

export interface CanonicalTreeLegacyGenerationProvenanceEvidence {
  readonly source:
    CanonicalTreeLegacyGenerationProvenanceSource;

  readonly semantics:
    CanonicalTreeLegacyGenerationEvidenceSemantics;

  readonly ownerUserId: string;
  readonly subjectMemberId: string;

  /*
   * For EXPLICIT_PARENT_EDGE evidence this is the historical child /
   * descendant endpoint whose parent set explicitly contained subject.
   *
   * For IDENTITY_REFERENCE_ONLY it must normally remain null.
   */
  readonly endpointMemberId:
    string | null;

  /*
   * These booleans must be established by the trusted evidence loader.
   * The pure classifier never infers ownership or chronology.
   */
  readonly sameGraphProven: boolean;
  readonly chronologyProven: boolean;
}

export interface CanonicalTreeLegacyGenerationProvenanceDecision {
  readonly status:
    CanonicalTreeLegacyGenerationProvenanceStatus;

  readonly endpointMemberId:
    string | null;

  readonly evidenceCount: number;
  readonly explicitParentEdgeEvidenceCount: number;
  readonly qualifiedParentEdgeEvidenceCount: number;

  readonly containsPrivateIdentity: true;
  readonly publishable: false;
  readonly readOnly: true;

  /*
   * 003G3B is classification only.
   *
   * Even PROVEN_UNAMBIGUOUS_ENDPOINT does not authorize planning or
   * execution. A successor must independently revalidate current graph
   * state, actor authority, topology, branch, cycles, stale state,
   * descendants, rollback and post-write verification.
   */
  readonly repairPlanningEligible: false;
  readonly repairReady: false;
  readonly repairExecuted: false;
  readonly executionAllowed: false;
  readonly automaticRepairAllowed: false;

  readonly reason: string;
}

const legacyGenerationProvenanceDecision = (
  status:
    CanonicalTreeLegacyGenerationProvenanceStatus,
  endpointMemberId:
    string | null,
  evidenceCount:
    number,
  explicitParentEdgeEvidenceCount:
    number,
  qualifiedParentEdgeEvidenceCount:
    number,
  reason:
    string,
): CanonicalTreeLegacyGenerationProvenanceDecision =>
  Object.freeze({
    status,
    endpointMemberId,
    evidenceCount,
    explicitParentEdgeEvidenceCount,
    qualifiedParentEdgeEvidenceCount,
    containsPrivateIdentity: true,
    publishable: false,
    readOnly: true,
    repairPlanningEligible: false,
    repairReady: false,
    repairExecuted: false,
    executionAllowed: false,
    automaticRepairAllowed: false,
    reason,
  });

export function classifyCanonicalTreeLegacyGenerationContradictionProvenance(
  input: {
    readonly contradiction:
      CanonicalTreeLegacyGenerationContradiction | null;

    readonly evidence:
      readonly CanonicalTreeLegacyGenerationProvenanceEvidence[];
  },
): CanonicalTreeLegacyGenerationProvenanceDecision {
  const contradiction =
    input.contradiction;

  if (!contradiction) {
    return legacyGenerationProvenanceDecision(
      'NO_CONTRADICTION',
      null,
      0,
      0,
      0,
      'No viewer-relative generation contradiction was supplied.',
    );
  }

  if (
    !contradiction.ownerUserId ||
    !contradiction.memberId ||
    !Number.isInteger(
      contradiction.structuralDepth,
    ) ||
    !Number.isInteger(
      contradiction.claimedDepth,
    ) ||
    contradiction.structuralDepth <= 0 ||
    contradiction.claimedDepth <= 0
  ) {
    return legacyGenerationProvenanceDecision(
      'BLOCKED_INVALID_CONTRADICTION',
      null,
      input.evidence.length,
      0,
      0,
      'Contradiction identity and positive integer depths are required.',
    );
  }

  if (
    contradiction.structuralDepth ===
    contradiction.claimedDepth
  ) {
    return legacyGenerationProvenanceDecision(
      'NO_CONTRADICTION',
      null,
      input.evidence.length,
      0,
      0,
      'Claimed direct-ancestor depth already matches structural depth.',
    );
  }

  /*
   * Mixed owner/member evidence is not silently discarded. Supplying a
   * different graph or subject means the evidence set itself is unsafe.
   */
  if (
    input.evidence.some(
      item =>
        item.ownerUserId !==
          contradiction.ownerUserId ||
        item.subjectMemberId !==
          contradiction.memberId,
    )
  ) {
    return legacyGenerationProvenanceDecision(
      'BLOCKED_EVIDENCE_SCOPE_MISMATCH',
      null,
      input.evidence.length,
      0,
      0,
      'Provenance evidence crossed contradiction identity or graph scope.',
    );
  }

  if (
    input.evidence.length ===
    0
  ) {
    return legacyGenerationProvenanceDecision(
      'PROVENANCE_ABSENT',
      null,
      0,
      0,
      0,
      'No historical ancestry-endpoint evidence is available.',
    );
  }

  const parentEdgeEvidence =
    input.evidence.filter(
      item =>
        item.semantics ===
        'EXPLICIT_PARENT_EDGE',
    );

  if (
    parentEdgeEvidence.length ===
    0
  ) {
    return legacyGenerationProvenanceDecision(
      'PROVENANCE_INSUFFICIENT',
      null,
      input.evidence.length,
      0,
      0,
      'Historical references exist but none explicitly encode a parent edge.',
    );
  }

  const qualified =
    parentEdgeEvidence.filter(
      item => {
        const endpoint =
          item.endpointMemberId
            ?.trim() ??
          '';

        return (
          endpoint.length >
            0 &&
          endpoint !==
            contradiction.memberId &&
          item.sameGraphProven ===
            true &&
          item.chronologyProven ===
            true
        );
      },
    );

  if (
    qualified.length ===
    0
  ) {
    return legacyGenerationProvenanceDecision(
      'PROVENANCE_INSUFFICIENT',
      null,
      input.evidence.length,
      parentEdgeEvidence.length,
      0,
      'Parent-edge references exist but none have proven same-graph and chronology-safe endpoint authority.',
    );
  }

  const unsafeParentEdgeEvidenceCount =
    parentEdgeEvidence.length -
    qualified.length;

  const endpoints =
    Array.from(
      new Set(
        qualified.map(
          item =>
            item.endpointMemberId!.trim(),
        ),
      ),
    ).sort();

  if (
    endpoints.length !==
      1 ||
    unsafeParentEdgeEvidenceCount >
      0
  ) {
    return legacyGenerationProvenanceDecision(
      'PROVENANCE_AMBIGUOUS',
      null,
      input.evidence.length,
      parentEdgeEvidence.length,
      qualified.length,
      'Historical parent-edge evidence does not prove exactly one uncontested same-graph chronology-safe endpoint.',
    );
  }

  return legacyGenerationProvenanceDecision(
    'PROVEN_UNAMBIGUOUS_ENDPOINT',
    endpoints[0],
    input.evidence.length,
    parentEdgeEvidence.length,
    qualified.length,
    'Historical parent-edge evidence proves one uncontested same-graph chronology-safe descendant endpoint. Classification remains non-executable.',
  );
}

export function auditExistingTreeStructure(
  members: readonly ExistingTreeAuditMember[],
  viewerMemberId: string | null = null,
): ExistingTreeStructuralAudit {
  const live = members.filter(
    (member) => member.id && member.ownerUserId,
  );

  const byOwner = new Map<
    string,
    ExistingTreeAuditMember[]
  >();

  for (const member of live) {
    const rows = byOwner.get(member.ownerUserId) ?? [];
    rows.push(member);
    byOwner.set(member.ownerUserId, rows);
  }

  const findings: ExistingTreeAuditFinding[] = [];

  for (const ownerMembers of byOwner.values()) {
    const byId = new Map(
      ownerMembers.map((member) => [
        member.id,
        member,
      ]),
    );

    const referencedAsParent = new Set<string>();
    const referencedAsSpouse = new Set<string>();

    for (const member of ownerMembers) {
      for (const parentId of member.parentIds) {
        referencedAsParent.add(parentId);
      }

      if (member.spouseId) {
        referencedAsSpouse.add(member.spouseId);
      }
    }

    for (const member of ownerMembers) {
      const parentIds = Array.from(
        new Set(member.parentIds.filter(Boolean)),
      );

      const missingParents = parentIds.filter(
        (parentId) => !byId.has(parentId),
      );

      if (missingParents.length > 0) {
        findings.push({
          kind: 'dangling-parent-reference',
          severity: 'blocking',
          memberId: member.id,
          memberName: member.name,
          relatedIds: missingParents,
          summary:
            `${member.name} references ${missingParents.length} parent row(s) that do not exist in the same family tree.`,
          automaticRepairAllowed: false,
        });
      }

      if (parentIds.includes(member.id)) {
        findings.push({
          kind: 'self-parent-reference',
          severity: 'blocking',
          memberId: member.id,
          memberName: member.name,
          relatedIds: [member.id],
          summary:
            `${member.name} is listed as their own parent.`,
          automaticRepairAllowed: false,
        });
      }

      if (parentIds.length >= 3) {
        findings.push({
          kind: 'excessive-parent-count',
          severity: 'warning',
          memberId: member.id,
          memberName: member.name,
          relatedIds: parentIds,
          summary:
            `${member.name} has ${parentIds.length} parent links and requires relationship-role review before any edge is removed.`,
          automaticRepairAllowed: false,
        });
      }

      const structurallyConnected =
        parentIds.some((id) => byId.has(id)) ||
        Boolean(
          member.spouseId &&
          byId.has(member.spouseId),
        ) ||
        referencedAsParent.has(member.id) ||
        referencedAsSpouse.has(member.id);

      if (
        member.claimedByUserId &&
        !structurallyConnected
      ) {
        findings.push({
          kind: 'unattached-claimed-profile',
          severity: 'warning',
          memberId: member.id,
          memberName: member.name,
          relatedIds: [],
          summary:
            `${member.name} is claimed but has no structural family connection in this tree.`,
          automaticRepairAllowed: false,
        });
      }

      /*
       * CC-TREE-LIVE-003G3B
       *
       * Deep ancestry is ordinary KinsCircle topology.
       *
       * A Great-Great-Grandparent MUST be the direct parent of the
       * Great-Grandparent immediately below them. Parent participation
       * is therefore never itself a contradiction.
       *
       * Contradiction authority comes only from viewer-relative
       * structural depth compared with an explicitly encoded canonical
       * direct-ancestor presentation depth.
       *
       * No viewer identity -> no generation inference.
       * No parseable direct-ancestor title -> no presentation-depth
       * assertion to compare.
       */
      const claimedAncestorDepth =
        directAncestorDepthFromTitle(
          member.relationshipOverride,
        );

      const structuralAncestorEvidence =
        viewerMemberId
          ? resolveCanonicalTreeDirectAncestorEvidence({
              members:
                ownerMembers as unknown as GraphMember[],
              viewerMemberId,
              ancestorMemberId:
                member.id,
            })
          : null;

      const structuralAncestorDepth =
        structuralAncestorEvidence?.status ===
          'PROVEN'
          ? structuralAncestorEvidence.exactDepth
          : null;

      if (
        claimedAncestorDepth !==
          null &&
        structuralAncestorDepth !==
          null &&
        claimedAncestorDepth !==
          structuralAncestorDepth
      ) {
        const directChildren =
          ownerMembers
            .filter(
              candidate =>
                candidate.parentIds.includes(
                  member.id,
                ),
            )
            .map(
              candidate =>
                candidate.id,
            );

        findings.push({
          kind:
            'direct-ancestor-depth-mismatch',
          severity:
            'warning',
          memberId:
            member.id,
          memberName:
            member.name,
          relatedIds:
            directChildren,
          summary:
            `${member.name} claims direct-ancestor depth ${claimedAncestorDepth} but canonical viewer-relative topology places this member at depth ${structuralAncestorDepth}. Historical endpoint provenance is required before any parent edge changes.`,
          automaticRepairAllowed:
            false,
        });
      }

      if (
        member.nickname?.trim() &&
        member.treeNamePreference !== 'nickname'
      ) {
        findings.push({
          kind: 'nickname-preference-available',
          severity: 'info',
          memberId: member.id,
          memberName: member.name,
          relatedIds: [],
          summary:
            `${member.name} has a nickname available but currently displays the primary name on the Tree.`,
          automaticRepairAllowed: false,
        });
      }
    }
  }

  return {
    scanned: live.length,
    findingCount: findings.length,
    blockingCount: findings.filter(
      (finding) => finding.severity === 'blocking',
    ).length,
    warningCount: findings.filter(
      (finding) => finding.severity === 'warning',
    ).length,
    informationalCount: findings.filter(
      (finding) => finding.severity === 'info',
    ).length,
    findings,
    productionWritesAllowed: false,
  };
}



/*
 * CC-TREE-LIVE-003G3D
 *
 * WHOLE_TREE_STABILITY_REGRESSION_CONTAINMENT_AUTHORITY
 *
 * Composed into the existing structural-audit root so 003G3D does not
 * alter the governed 003G2W application TypeScript root-file set.
 *
 * This authority is read-only and fail-closed. It does not repair or
 * mutate production topology.
 */

export type TreeStabilitySeverity =
  | 'info'
  | 'warning'
  | 'blocking';

export type TreeStabilityStatus =
  | 'READY'
  | 'READY_WITH_WARNINGS'
  | 'BLOCKED';

export type TreeStabilityFindingKind =
  | 'SOURCE_DANGLING_PARENT_REFERENCE'
  | 'SOURCE_SELF_PARENT_REFERENCE'
  | 'SOURCE_EXCESSIVE_PARENT_COUNT'
  | 'SOURCE_UNATTACHED_CLAIMED_PROFILE'
  | 'SOURCE_DIRECT_ANCESTOR_DEPTH_MISMATCH'
  | 'SOURCE_CROSS_GRAPH_PARENT_REFERENCE'
  | 'SOURCE_ANCESTRY_CYCLE'
  | 'SOURCE_DANGLING_SPOUSE_REFERENCE'
  | 'SOURCE_CROSS_GRAPH_SPOUSE_REFERENCE'
  | 'SOURCE_SELF_SPOUSE_REFERENCE'
  | 'SOURCE_NONRECIPROCAL_SPOUSE_REFERENCE'
  | 'SOURCE_VIEWER_IDENTITY_UNRESOLVED'
  | 'SOURCE_PROJECTION_PARENT_EDGE_MISSING'
  | 'VISIBLE_MEMBER_PROJECTION_MISSING'
  | 'PROJECTION_RENDER_PARENT_EDGE_MISSING'
  | 'SOURCE_AUDIT_INFORMATION';

export type TreeStabilityMember =
  ExistingTreeAuditMember;

export interface TreeStabilitySourceAuditFinding {
  readonly kind: string;
  readonly severity: string;
  readonly memberId: string;
  readonly relatedIds: readonly string[];
}

export interface TreeStabilityCanonicalEdge {
  readonly sourceMemberId: string;
  readonly targetMemberId: string;
  readonly kind: string;
  readonly direction: string;
  readonly status: string;
  readonly createsAncestry: boolean;
}

export type TreeStabilityRenderedRouteStatus =
  | 'ROUTED'
  | 'REJECTED-NONADJACENT-GENERATION'
  | 'REJECTED-ROOT-BOUNDARY'
  | 'REJECTED-OBSTACLE-INTERSECTION';

export interface TreeStabilityRenderedEdge {
  readonly fromMemberIds: readonly string[];
  readonly toMemberId: string;
  readonly routeStatus:
    TreeStabilityRenderedRouteStatus;
  readonly routePointCount: number;
}

export interface TreeStabilityFinding {
  readonly kind: TreeStabilityFindingKind;
  readonly severity: TreeStabilitySeverity;
  readonly memberIds: readonly string[];
  readonly edgeKey: string | null;
  readonly reason: string;
}

export interface TreeStabilityRegressionContainmentInput {
  readonly members: readonly TreeStabilityMember[];
  readonly viewerMemberId: string | null;
  readonly canonicalEdges: readonly TreeStabilityCanonicalEdge[];
  readonly projectedMemberIds?: readonly string[];
  readonly visibleMemberIds?: readonly string[];
  readonly renderedEdges?: readonly TreeStabilityRenderedEdge[];
}

export interface TreeStabilityRegressionContainmentSummary {
  readonly activeMemberCount: number;
  readonly activeGraphCount: number;
  readonly sourceParentEdgeCount: number;
  readonly canonicalParentEdgeCount: number;
  readonly renderedEdgeCount: number;
  readonly blockingFindingCount: number;
  readonly warningFindingCount: number;
  readonly informationalFindingCount: number;
}

export interface TreeStabilityRegressionContainmentResult {
  readonly authority: 'CC-TREE-LIVE-003G3D';
  readonly status: TreeStabilityStatus;
  readonly findings: readonly TreeStabilityFinding[];
  readonly summary: TreeStabilityRegressionContainmentSummary;
  readonly containsPrivateIdentity: true;
  readonly publishable: false;
  readonly readOnly: true;
  readonly productionWritesAllowed: false;
  readonly automaticRepairAllowed: false;
  readonly regressionContainmentEnforced: true;
  readonly repairRequired: boolean;
}

const treeStabilityMemberIsActive = (
  member: TreeStabilityMember,
): boolean =>
  member.id.length > 0 &&
  member.ownerUserId.length > 0 &&
  member.approvalStatus !== 'rejected' &&
  member.profileState !== 'deleted';

const treeStabilityParentEdgeKey = (
  parentId: string,
  childId: string,
): string =>
  `${parentId}->${childId}`;

const treeStabilityPairKey = (
  left: string,
  right: string,
): string =>
  left < right
    ? `${left}::${right}`
    : `${right}::${left}`;

const treeStabilitySeverityRank = (
  severity: TreeStabilitySeverity,
): number =>
  severity === 'blocking'
    ? 0
    : severity === 'warning'
      ? 1
      : 2;

const treeStabilitySourceAuditMapping = (
  finding: TreeStabilitySourceAuditFinding,
): {
  kind: TreeStabilityFindingKind;
  severity: TreeStabilitySeverity;
} => {
  switch (finding.kind) {
    case 'dangling-parent-reference':
      return {
        kind: 'SOURCE_DANGLING_PARENT_REFERENCE',
        severity: 'blocking',
      };
    case 'self-parent-reference':
      return {
        kind: 'SOURCE_SELF_PARENT_REFERENCE',
        severity: 'blocking',
      };
    case 'direct-ancestor-depth-mismatch':
      return {
        kind: 'SOURCE_DIRECT_ANCESTOR_DEPTH_MISMATCH',
        severity: 'blocking',
      };
    case 'excessive-parent-count':
      return {
        kind: 'SOURCE_EXCESSIVE_PARENT_COUNT',
        severity: 'warning',
      };
    case 'unattached-claimed-profile':
      return {
        kind: 'SOURCE_UNATTACHED_CLAIMED_PROFILE',
        severity: 'warning',
      };
    default:
      return {
        kind: 'SOURCE_AUDIT_INFORMATION',
        severity:
          finding.severity === 'blocking'
            ? 'blocking'
            : finding.severity === 'warning'
              ? 'warning'
              : 'info',
      };
  }
};

const treeStabilityCycleFrom = (
  startId: string,
  byId: ReadonlyMap<string, TreeStabilityMember>,
): readonly string[] | null => {
  const start = byId.get(startId);
  if (!start) return null;

  const stack: Array<{
    id: string;
    path: string[];
  }> = [{ id: startId, path: [startId] }];

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;

    const member = byId.get(current.id);
    if (!member) continue;

    for (const parentId of member.parentIds ?? []) {
      const parent = byId.get(parentId);
      if (!parent || parent.ownerUserId !== start.ownerUserId) {
        continue;
      }

      if (parentId === startId) {
        return Object.freeze([
          ...current.path,
          startId,
        ]);
      }

      if (current.path.includes(parentId)) {
        continue;
      }

      stack.push({
        id: parentId,
        path: [...current.path, parentId],
      });
    }
  }

  return null;
};

export const evaluateTreeStabilityRegressionContainment = (
  input: TreeStabilityRegressionContainmentInput,
): TreeStabilityRegressionContainmentResult => {
  const activeMembers = input.members.filter(treeStabilityMemberIsActive);
  const byId = new Map(
    activeMembers.map(member => [member.id, member] as const),
  );

  const findings: TreeStabilityFinding[] = [];
  const findingKeys = new Set<string>();

  const addFinding = (
    kind: TreeStabilityFindingKind,
    severity: TreeStabilitySeverity,
    memberIds: readonly string[],
    edgeKey: string | null,
    reason: string,
  ) => {
    const normalizedMemberIds = [...new Set(memberIds)].sort();
    const key = [
      kind,
      edgeKey ?? '',
      normalizedMemberIds.join(','),
    ].join('|');

    if (findingKeys.has(key)) return;
    findingKeys.add(key);

    findings.push(Object.freeze({
      kind,
      severity,
      memberIds: Object.freeze(normalizedMemberIds),
      edgeKey,
      reason,
    }));
  };

  if (
    activeMembers.length > 0 &&
    (
      !input.viewerMemberId ||
      !byId.has(input.viewerMemberId)
    )
  ) {
    addFinding(
      'SOURCE_VIEWER_IDENTITY_UNRESOLVED',
      'blocking',
      input.viewerMemberId
        ? [input.viewerMemberId]
        : [],
      null,
      'Viewer-relative stability cannot be certified without an active viewer member.',
    );
  }

  const sourceAudit =
    auditExistingTreeStructure(
      activeMembers,
      input.viewerMemberId,
    );

  if (
    input.viewerMemberId &&
    !byId.has(input.viewerMemberId)
  ) {
    addFinding(
      'SOURCE_VIEWER_IDENTITY_UNRESOLVED',
      'blocking',
      [input.viewerMemberId],
      null,
      'The supplied viewer identity does not resolve to an active member in the stability graph.',
    );
  }

  for (const sourceFinding of sourceAudit.findings) {
    const mapped = treeStabilitySourceAuditMapping(sourceFinding);
    addFinding(
      mapped.kind,
      mapped.severity,
      [sourceFinding.memberId, ...sourceFinding.relatedIds],
      null,
      `Existing structural audit reported ${sourceFinding.kind}.`,
    );
  }

  const sourceParentEdges = new Set<string>();

  for (const child of activeMembers) {
    for (const parentId of [...new Set(child.parentIds ?? [])]) {
      if (!parentId) continue;

      const parent = byId.get(parentId);
      if (!parent) {
        continue;
      }

      if (parent.ownerUserId !== child.ownerUserId) {
        addFinding(
          'SOURCE_CROSS_GRAPH_PARENT_REFERENCE',
          'blocking',
          [parent.id, child.id],
          treeStabilityParentEdgeKey(parent.id, child.id),
          'A parent reference crosses family-graph ownership.',
        );
        continue;
      }

      if (parent.id === child.id) {
        addFinding(
          'SOURCE_SELF_PARENT_REFERENCE',
          'blocking',
          [child.id],
          treeStabilityParentEdgeKey(child.id, child.id),
          'A member references itself as a parent.',
        );
        continue;
      }

      sourceParentEdges.add(
        treeStabilityParentEdgeKey(parent.id, child.id),
      );
    }
  }

  for (const member of activeMembers) {
    const cycle = treeStabilityCycleFrom(member.id, byId);
    if (cycle) {
      addFinding(
        'SOURCE_ANCESTRY_CYCLE',
        'blocking',
        cycle,
        null,
        'Parent references create an ancestry cycle.',
      );
    }
  }

  const seenSpousePairs = new Set<string>();

  for (const member of activeMembers) {
    if (!member.spouseId) continue;

    if (member.spouseId === member.id) {
      addFinding(
        'SOURCE_SELF_SPOUSE_REFERENCE',
        'blocking',
        [member.id],
        null,
        'A member references itself as spouse.',
      );
      continue;
    }

    const partner = byId.get(member.spouseId);
    if (!partner) {
      addFinding(
        'SOURCE_DANGLING_SPOUSE_REFERENCE',
        'blocking',
        [member.id, member.spouseId],
        null,
        'A spouse reference points to no active member.',
      );
      continue;
    }

    if (partner.ownerUserId !== member.ownerUserId) {
      addFinding(
        'SOURCE_CROSS_GRAPH_SPOUSE_REFERENCE',
        'blocking',
        [member.id, partner.id],
        null,
        'A spouse reference crosses family-graph ownership.',
      );
      continue;
    }

    const pair = treeStabilityPairKey(member.id, partner.id);
    if (seenSpousePairs.has(pair)) continue;
    seenSpousePairs.add(pair);

    if (partner.spouseId !== member.id) {
      addFinding(
        'SOURCE_NONRECIPROCAL_SPOUSE_REFERENCE',
        'blocking',
        [member.id, partner.id],
        null,
        'A current spouse pointer is not reciprocal.',
      );
    }
  }

  const canonicalParentEdges = new Set(
    input.canonicalEdges
      .filter(edge =>
        edge.kind === 'parent-child' &&
        edge.direction === 'directed' &&
        edge.status === 'active' &&
        edge.createsAncestry === true,
      )
      .map(edge =>
        treeStabilityParentEdgeKey(
          edge.sourceMemberId,
          edge.targetMemberId,
        ),
      ),
  );

  for (const edgeKey of sourceParentEdges) {
    if (!canonicalParentEdges.has(edgeKey)) {
      const [parentId, childId] = edgeKey.split('->');
      addFinding(
        'SOURCE_PROJECTION_PARENT_EDGE_MISSING',
        'blocking',
        [parentId, childId],
        edgeKey,
        'A valid stored parent-child edge is missing from canonical projection evidence.',
      );
    }
  }

  const projectedMemberIds =
    input.projectedMemberIds
      ? new Set(input.projectedMemberIds)
      : null;

  const visibleMemberIds =
    input.visibleMemberIds
      ? new Set(input.visibleMemberIds)
      : null;

  if (projectedMemberIds && visibleMemberIds) {
    for (const memberId of visibleMemberIds) {
      if (!projectedMemberIds.has(memberId)) {
        addFinding(
          'VISIBLE_MEMBER_PROJECTION_MISSING',
          'blocking',
          [memberId],
          null,
          'A member selected for production visibility is absent from canonical projection.',
        );
      }
    }
  }

  if (visibleMemberIds && input.renderedEdges) {
    const renderedParentEdges = new Set<string>();

    for (const edge of input.renderedEdges) {
      if (
        edge.routeStatus !== 'ROUTED' ||
        edge.routePointCount < 2
      ) {
        continue;
      }

      for (const parentId of edge.fromMemberIds) {
        renderedParentEdges.add(
          treeStabilityParentEdgeKey(parentId, edge.toMemberId),
        );
      }
    }

    for (const edgeKey of sourceParentEdges) {
      const [parentId, childId] = edgeKey.split('->');

      if (
        !visibleMemberIds.has(parentId) ||
        !visibleMemberIds.has(childId) ||
        !canonicalParentEdges.has(edgeKey)
      ) {
        continue;
      }

      if (!renderedParentEdges.has(edgeKey)) {
        addFinding(
          'PROJECTION_RENDER_PARENT_EDGE_MISSING',
          'blocking',
          [parentId, childId],
          edgeKey,
          'A canonical visible parent-child edge is absent from rendered connector evidence.',
        );
      }
    }
  }

  findings.sort((left, right) =>
    treeStabilitySeverityRank(left.severity) - treeStabilitySeverityRank(right.severity) ||
    left.kind.localeCompare(right.kind) ||
    (left.edgeKey ?? '').localeCompare(right.edgeKey ?? '') ||
    left.memberIds.join(',').localeCompare(right.memberIds.join(',')),
  );

  const blockingFindingCount =
    findings.filter(finding => finding.severity === 'blocking').length;
  const warningFindingCount =
    findings.filter(finding => finding.severity === 'warning').length;
  const informationalFindingCount =
    findings.filter(finding => finding.severity === 'info').length;

  const status: TreeStabilityStatus =
    blockingFindingCount > 0
      ? 'BLOCKED'
      : warningFindingCount > 0
        ? 'READY_WITH_WARNINGS'
        : 'READY';

  const activeGraphCount =
    new Set(activeMembers.map(member => member.ownerUserId)).size;

  const summary: TreeStabilityRegressionContainmentSummary =
    Object.freeze({
      activeMemberCount: activeMembers.length,
      activeGraphCount,
      sourceParentEdgeCount: sourceParentEdges.size,
      canonicalParentEdgeCount: canonicalParentEdges.size,
      renderedEdgeCount: input.renderedEdges?.length ?? 0,
      blockingFindingCount,
      warningFindingCount,
      informationalFindingCount,
    });

  return Object.freeze({
    authority: 'CC-TREE-LIVE-003G3D',
    status,
    findings: Object.freeze(findings),
    summary,
    containsPrivateIdentity: true,
    publishable: false,
    readOnly: true,
    productionWritesAllowed: false,
    automaticRepairAllowed: false,
    regressionContainmentEnforced: true,
    repairRequired: blockingFindingCount > 0,
  });
};
